"use client";

import { dismissHealthStatusBanner } from "@/app/actions/health-status-banner";
import { getMetriportConnectionStatus } from "@/app/queries/metriport";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AlertCircle, CheckCircle2, Info, RefreshCw } from "lucide-react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { Button } from "../ui/button";

export default function HealthStatusBanner() {
    const router = useRouter();
    const { data: session } = useSession();
    // Initialize with cached status from localStorage if available
    const [localMetriportStatus, setLocalMetriportStatus] = useState<
        string | undefined
    >(() => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("cachedMetriportStatus") || undefined;
        }
        return undefined;
    });
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [isLoadingStatus, setIsLoadingStatus] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [localBannerDismissed, setLocalBannerDismissed] = useState(false);

    // Get dismissal state from user session data
    const patient = session?.user as any; // Cast for potential custom fields
    const sessionBannerDismissed =
        patient?.health_status_banner_dismissed || false;
    // Use local state if dismissed, otherwise fall back to session data
    const bannerDismissed = localBannerDismissed || sessionBannerDismissed;

    // Mark initial load as complete when session is available
    useEffect(() => {
        if (session) {
            setInitialLoadComplete(true);
        }
    }, [session]);

    // Fetch Metriport connection status
    useEffect(() => {
        async function fetchMetriportStatus() {
            if (session?.user) {
                try {
                    setIsLoadingStatus(true);
                    const response = await getMetriportConnectionStatus();

                    if (response.type === "success" && response.data) {
                        const newStatus = response.data.status;
                        setLocalMetriportStatus(newStatus);
                        // Cache the status in localStorage
                        if (typeof window !== "undefined") {
                            localStorage.setItem(
                                "cachedMetriportStatus",
                                newStatus,
                            );
                        }
                    } else {
                        // If no connection found, set status to undefined
                        setLocalMetriportStatus(undefined);
                        // Clear cached status
                        if (typeof window !== "undefined") {
                            localStorage.removeItem("cachedMetriportStatus");
                        }
                    }
                } catch (error) {
                    console.error("Error fetching Metriport status:", error);
                    // Set status to undefined on error
                    setLocalMetriportStatus(undefined);
                } finally {
                    setIsLoadingStatus(false);
                }
            }
        }

        fetchMetriportStatus();
    }, [session]);

    // If we have a successful API response but no data, we should show the "Connect" banner
    // Otherwise, use the metriport_id from the patient object
    const hasNoConnection = localMetriportStatus === undefined;
    const metriportIdForCheck = hasNoConnection ? null : patient?.metriport_id; // Used to check if Metriport connection exists
    const patientIdForStream = patient?.id; // User's primary ID, used for SSE subscription
    const currentMetriportStatus = localMetriportStatus;

    // useEffect for Server-Sent Events (SSE) for real-time status updates
    // Only subscribe to Redis if status is pending or processing
    useEffect(() => {
        let eventSource: EventSource | null = null;

        // Only connect to SSE if the status is pending or processing
        const shouldConnectSse =
            !isLoadingStatus && // Make sure we've finished loading the status
            metriportIdForCheck &&
            patientIdForStream && // Also ensure we have the patientId for the stream
            (currentMetriportStatus === "pending" ||
                currentMetriportStatus === "processing");

        if (shouldConnectSse) {
            console.log(
                `HealthStatusBanner: Initializing SSE connection for patient ID: ${patientIdForStream} (Metriport ID for check: ${metriportIdForCheck})`,
            );
            // Use patientIdForStream for the EventSource URL as this matches the ID in Redis messages
            eventSource = new EventSource(
                `/api/metriport/status-stream?metriportId=${encodeURIComponent(patientIdForStream)}`,
            );

            eventSource.onopen = () => {
                console.log("HealthStatusBanner: SSE connection opened.");
            };

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.status && data.status !== currentMetriportStatus) {
                        console.log(
                            `HealthStatusBanner: SSE received status update: ${data.status}`,
                        );
                        setLocalMetriportStatus(data.status);

                        if (
                            data.status === "completed" ||
                            data.status === "failed"
                        ) {
                            console.log(
                                "HealthStatusBanner: SSE connection closing due to final status.",
                            );
                            eventSource?.close();
                        }
                    }
                } catch (error) {
                    console.error(
                        "HealthStatusBanner: Error parsing SSE message or updating status:",
                        error,
                    );
                }
            };

            eventSource.onerror = (error) => {
                console.error(
                    "HealthStatusBanner: SSE connection error:",
                    error,
                );
                // Depending on the error, you might want to close and attempt to reconnect
                // or just let it be if it's a persistent server issue.
                // For now, just log. EventSource typically attempts to reconnect automatically.
                // If it's a fatal error (e.g., 404), it won't reconnect.
                // Consider closing if error indicates a non-recoverable issue.
                // eventSource?.close();
            };
        }

        return () => {
            if (eventSource) {
                console.log(
                    "HealthStatusBanner: Closing SSE connection on cleanup.",
                );
                eventSource.close();
            }
        };
    }, [
        metriportIdForCheck,
        patientIdForStream,
        currentMetriportStatus,
        isLoadingStatus,
    ]); // Added isLoadingStatus dependency

    const handleDismiss = async () => {
        try {
            // Set local state immediately for instant UI update
            setLocalBannerDismissed(true);
            // Update the database
            await dismissHealthStatusBanner();
        } catch (error) {
            console.error("Error dismissing health status banner:", error);
            // Revert local state if database update failed
            setLocalBannerDismissed(false);
        }
    };

    // Memoize the banner content based on status and dismissal state
    const bannerContent = useMemo(() => {
        // If user has dismissed the banner, don't show any banner for completed/failed status
        if (
            bannerDismissed &&
            (localMetriportStatus === "completed" ||
                localMetriportStatus === "failed")
        ) {
            return null;
        }

        // If we have a status, use it to determine the banner
        if (localMetriportStatus === "completed" && !bannerDismissed) {
            // Completed status and not dismissed - show completed banner
            return (
                <div
                    className="bg-green-50 text-green-800 p-6 rounded-lg"
                    role="alert"
                >
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <CheckCircle2 className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-3 flex-grow">
                            <h3 className="text-lg font-semibold mb-2">
                                Health Records Connected!
                            </h3>
                            <p className="text-sm mb-4">
                                Your health records are successfully connected.
                            </p>
                        </div>
                    </div>
                    <Button
                        className="w-full mt-2 bg-rthm-dark-blue text-white"
                        onClick={async () => {
                            await handleDismiss();
                            router.push("/chat?query=Summarize my records");
                        }}
                    >
                        Start your personalized welcome chat
                    </Button>
                </div>
            );
        } else if (localMetriportStatus === "failed" && !bannerDismissed) {
            // Failed status and not dismissed - show failed banner
            return (
                <div
                    className="bg-red-50 text-red-800 p-6 rounded-lg"
                    role="alert"
                >
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <AlertCircle className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="ml-3 flex-grow">
                            <h3 className="text-lg font-semibold mb-2">
                                Connection Failed
                            </h3>
                            <p className="text-sm mb-4">
                                There was an issue connecting your health
                                records. Please try again.
                            </p>
                        </div>
                    </div>
                    <Button
                        className="w-full mt-2 bg-rthm-dark-blue text-white"
                        onClick={async () => {
                            await handleDismiss();
                            router.push("/chat");
                        }}
                    >
                        Try Again
                    </Button>
                </div>
            );
        } else if (
            localMetriportStatus === "pending" ||
            localMetriportStatus === "processing"
        ) {
            // Pending or processing status - show in progress banner
            return (
                <div
                    className="bg-yellow-50 text-yellow-800 p-6 rounded-lg"
                    role="alert"
                >
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <RefreshCw className="h-6 w-6 text-yellow-600 animate-spin" />
                        </div>
                        <div className="ml-3 flex-grow">
                            <h3 className="text-lg font-semibold mb-2">
                                Health Records Connection In Progress
                            </h3>
                            <p className="text-sm">
                                The health records connection is started. We
                                will let you know when it is done.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        // If we get here, don't show any banner
        return null;
    }, [localMetriportStatus, bannerDismissed, handleDismiss, router]);

    // Effect to control dialog open state - only for completed/failed status
    useEffect(() => {
        if (
            initialLoadComplete &&
            session &&
            patient &&
            bannerContent &&
            (localMetriportStatus === "completed" ||
                localMetriportStatus === "failed")
        ) {
            setIsDialogOpen(true);
        } else {
            setIsDialogOpen(false);
        }
    }, [
        initialLoadComplete,
        session,
        patient,
        bannerContent,
        localMetriportStatus,
    ]);

    const handleDialogClose = async () => {
        setIsDialogOpen(false);
        // If it's a dismissible status, mark it as dismissed
        if (
            (localMetriportStatus === "completed" ||
                localMetriportStatus === "failed") &&
            !bannerDismissed
        ) {
            await handleDismiss();
        }
    };

    if (
        !initialLoadComplete ||
        !session ||
        !patient ||
        !session.user.has_completed_app_tour
    ) {
        return null;
    }

    // Determine dialog background color based on status
    const getDialogBgClass = () => {
        if (localMetriportStatus === "completed" && !bannerDismissed) {
            return "bg-green-50";
        } else if (localMetriportStatus === "failed" && !bannerDismissed) {
            return "bg-red-50";
        } else if (
            localMetriportStatus === "pending" ||
            localMetriportStatus === "processing"
        ) {
            return "bg-yellow-50";
        }
        return "bg-white";
    };

    // Check if we should show in-progress as a banner
    const shouldShowBanner =
        initialLoadComplete &&
        session &&
        patient &&
        bannerContent &&
        (localMetriportStatus === "pending" ||
            localMetriportStatus === "processing");

    return (
        <>
            {/* Show in-progress status as a banner */}
            {shouldShowBanner && (
                <div className="w-full sticky top-0 z-50">{bannerContent}</div>
            )}

            {/* Show completed/failed status as a dialog */}
            <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
                <DialogContent
                    className={`max-w-md p-0 mt-0 ${getDialogBgClass()}`}
                >
                    <DialogTitle className="sr-only">
                        Health Status Update
                    </DialogTitle>
                    {bannerContent}
                </DialogContent>
            </Dialog>
        </>
    );
}
